export interface FleetSummaryResponse {
  onlineAgents: number;
  offlineAgents: number;
  lastUpdated?: string;
  _demo?: boolean;
}

export class ApiError extends Error {
  status?: number;
  url?: string;

  constructor(message: string, options?: { status?: number; url?: string }) {
    super(message);
    this.name = "ApiError";
    this.status = options?.status;
    this.url = options?.url;
  }
}

function ensureApiBaseUrl(): string {
  const url =
    import.meta.env.VITE_API_BASE_URL ??
    // Backwards-compat for older CI/secrets naming.
    import.meta.env.VITE_PUBLIC_API_URL;

  if (!url) {
    // Hard fallback: prefer the stable custom domain in production.
    // Keep localhost ergonomics for local development.
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    if (host === "localhost" || host === "127.0.0.1" || host === "::1") {
      return "http://localhost:3000";
    }
    return "https://api.verityedge.eu";
  }

  return String(url).replace(/\/$/, "");
}

export function getApiBaseUrl(): string {
  return ensureApiBaseUrl();
}

function getOptionalAuthToken(): string | undefined {
  try {
    const token =
      window.localStorage.getItem("verityedge_jwt") ||
      window.localStorage.getItem("auth_token") ||
      window.localStorage.getItem("token");

    if (token && token.trim().length > 0) {
      return token.trim();
    }

    // Supabase Auth (v2) stores session under keys like: sb-<project-ref>-auth-token
    // We opportunistically read it so the backend can receive an Authorization bearer.
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (!key || !key.startsWith("sb-") || !key.endsWith("-auth-token")) continue;

      const raw = window.localStorage.getItem(key);
      if (!raw) continue;

      try {
        const parsed = JSON.parse(raw) as any;
        const accessToken =
          parsed?.access_token ||
          parsed?.currentSession?.access_token ||
          parsed?.session?.access_token;
        if (typeof accessToken === "string" && accessToken.trim().length > 0) {
          return accessToken.trim();
        }
      } catch {
        // ignore parsing errors
      }
    }

    return undefined;
  } catch {
    return undefined;
  }
}

async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  const signal = init.signal ? init.signal : controller.signal;

  try {
    return await fetch(url, { ...init, signal });
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export async function fetchFleetSummary(apiBaseUrl: string): Promise<FleetSummaryResponse> {
  const token = getOptionalAuthToken();
  const primaryUrl = `${apiBaseUrl}/api/v1/fleet/summary`;

  const primaryHeaders: Record<string, string> = { Accept: "application/json" };
  if (token) {
    primaryHeaders.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetchWithTimeout(
      primaryUrl,
      {
        headers: primaryHeaders,
        credentials: "include"
      },
      10_000
    );
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (message.includes("AbortError")) {
      throw new ApiError("Fleet metrics request timed out", { url: primaryUrl });
    }
    throw new ApiError(`Fleet metrics request failed: ${message}`, { url: primaryUrl });
  }

  if (response.ok) {
    return (await response.json()) as FleetSummaryResponse;
  }

  // If auth is missing/invalid, try explicit demo/public endpoint (if enabled on the backend).
  if (response.status === 401 || response.status === 403) {
    const demoUrl = `${apiBaseUrl}/api/v1/public/fleet/summary`;
    const demoRes = await fetchWithTimeout(
      demoUrl,
      {
        headers: { Accept: "application/json" },
        credentials: "include"
      },
      10_000
    );
    if (demoRes.ok) {
      return (await demoRes.json()) as FleetSummaryResponse;
    }
    throw new ApiError(`Failed to load fleet summary (demo fallback): ${demoRes.status}`, {
      status: demoRes.status,
      url: demoUrl
    });
  }

  throw new ApiError(`Failed to load fleet summary: ${response.status}`, {
    status: response.status,
    url: primaryUrl
  });
}
